import React, { useState } from "react";
import { BriefcaseBusiness, Download, Eye, FileText, History } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  EmptyState, FileViewerModal,
} from "../ui";
import CareerProfileCard from "./CareerProfileCard";
import ProjectsSection from "./ProjectsSection";
import AchievementsSection from "./AchievementsSection";
import CertificationsSection from "./CertificationsSection";
import VideosSection from "./VideosSection";
import ResumeReport, { ScorePill } from "./ResumeReport";
import { fmtDate } from "./constants";
import { fetchStudentAnalyses } from "./portfolioApi";

/**
 * StudentPortfolioSections — a student's portfolio, read-only, for staff.
 *
 * Rendered by the instructor route (`StudentPortfolioView`) and by the admin
 * student record's Portfolio tab. It takes the `GET /portfolio/student/:id` body
 * instead of fetching it, because the student record reads the resume score and
 * the earned credentials out of that same response for its other tabs.
 *
 * Every section reuses the student's own component with `readOnly`, so a staff
 * reader sees exactly the portfolio the student built. There is no separate
 * "staff rendering" to drift out of step.
 */
const StudentPortfolioSections = ({ studentId, data }) => {
  const [viewingResume, setViewingResume] = useState(false);
  const [history, setHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const toggleHistory = async () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next && history === null) {
      try {
        const { analyses } = await fetchStudentAnalyses(studentId);
        setHistory((analyses || []).filter((a) => a.status === "completed"));
      } catch {
        setHistory([]);
      }
    }
  };

  const { portfolio, earned, latestAnalysis } = data || {};
  const resume = portfolio?.resume;

  if (!portfolio) {
    return (
      <EmptyState
        icon={BriefcaseBusiness}
        title="This student has not started a portfolio"
        description="Once they fill in their career profile and upload a resume, it will appear here."
      />
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <CareerProfileCard profile={portfolio.careerProfile} readOnly onSaved={() => {}} />

      <AppCard>
        <AppCardHeader className="tw-flex-row tw-flex-wrap tw-items-start tw-justify-between tw-gap-3">
          <div className="tw-min-w-0">
            <AppCardTitle className="[&_svg]:tw-text-primary">
              <FileText size={18} /> Resume
            </AppCardTitle>
            {resume?.fileName && (
              <p className="tw-m-0 tw-mt-1 tw-text-[0.8125rem] tw-text-content-muted">
                {resume.fileName}
                {resume.uploadedAt && ` · uploaded ${fmtDate(resume.uploadedAt)}`}
              </p>
            )}
          </div>
          {resume?.key && (
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
              {latestAnalysis?.status === "completed" && <ScorePill score={latestAnalysis.overallScore} />}
              {latestAnalysis && (
                <AppButton variant="ghost" size="sm" onClick={toggleHistory} aria-expanded={showHistory}>
                  <History size={16} /> Past scores
                </AppButton>
              )}
              <AppButton variant="outline" size="sm" onClick={() => setViewingResume(true)} disabled={!resume.url}>
                <Eye size={16} /> View resume
              </AppButton>
              {resume.url && (
                <AppButton variant="ghost" size="sm" as="a" href={resume.url} target="_blank" rel="noopener noreferrer">
                  <Download size={16} /> Open
                </AppButton>
              )}
            </div>
          )}
        </AppCardHeader>
        <AppCardContent>
          {!resume?.key ? (
            <EmptyState icon={FileText} title="No resume uploaded"
              description="This student has not uploaded a resume yet." />
          ) : !latestAnalysis ? (
            <EmptyState icon={FileText} title="Not analysed yet"
              description="The student has uploaded a resume but has not run the analysis. Only they can start it." />
          ) : (
            <div className="tw-flex tw-flex-col tw-gap-4">
              {showHistory && history !== null && (
                <div className="tw-flex tw-flex-wrap tw-gap-2">
                  {history.length === 0
                    ? <span className="tw-text-sm tw-text-content-subtle">No earlier analyses.</span>
                    : history.map((h) => (
                      <span key={h._id}
                        className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-solid tw-border-line-soft tw-px-2.5 tw-py-1 tw-text-xs tw-text-content-muted">
                        <strong className="tw-text-content">{h.overallScore}</strong>
                        {fmtDate(h.completedAt || h.createdAt)}
                      </span>
                    ))}
                </div>
              )}
              <ResumeReport analysis={latestAnalysis} />
            </div>
          )}
        </AppCardContent>
      </AppCard>

      <ProjectsSection projects={portfolio.projects} readOnly onPortfolioChange={() => {}} />
      <AchievementsSection achievements={portfolio.achievements} readOnly onPortfolioChange={() => {}} />
      <CertificationsSection
        certifications={portfolio.certifications}
        earned={earned}
        readOnly
        onPortfolioChange={() => {}}
      />
      <VideosSection
        videos={portfolio.showcaseVideos}
        projects={portfolio.projects || []}
        readOnly
        onPortfolioChange={() => {}}
      />

      <FileViewerModal
        show={viewingResume}
        onHide={() => setViewingResume(false)}
        fileUrl={resume?.url || ""}
        fileName={resume?.fileName || "Resume"}
        fileType={resume?.mimeType || "application/pdf"}
      />
    </div>
  );
};

export default StudentPortfolioSections;
